<template>
  <b-form-group class="multi-select" label-class="multi-select__label" :label-for="id" :label="label">
    <template v-if="$slots.label" #label><slot name="label"></slot></template>

    <b-dropdown
      :id="id"
      ref="$dropdown"
      class="multi-select__dropdown w-100"
      variant="light"
      :disabled="isDisabled"
      toggle-class="multi-select__toggle form-control d-flex align-items-center justify-content-between"
      no-caret
      @show="isOpen = true"
      @hide="isOpen = false"
      @shown="onDropdownShown"
    >
      <template #button-content>
        <div class="d-flex align-items-center justify-content-between w-100">
          <template v-if="isNoOptions">
            <div class="multi-select__tags">
              <b-badge variant="light" class="multi-select__tag" @click.stop>
                {{ selectAllText }}
              </b-badge>
            </div>
          </template>
          <template v-else-if="selectAll && isAllSelectedUi">
            <div class="multi-select__tags">
              <b-badge variant="light" class="multi-select__tag" @click.stop>
                {{ selectAllText }}
                <font-awesome-icon
                  v-if="isAllSelected"
                  :icon="['fal', 'times']"
                  class="ml-2"
                  @click.stop="listValue = []"
                />
              </b-badge>
            </div>
          </template>
          <template v-else-if="hasSelection">
            <div class="multi-select__tags">
              <b-badge
                v-for="opt in selectedOptions"
                :key="opt.value"
                variant="light"
                class="multi-select__tag"
                @click.stop
              >
                {{ opt.text }}
                <font-awesome-icon :icon="['fal', 'times']" class="ml-2" @click.stop="removeValue(opt.value)" />
              </b-badge>
            </div>
          </template>
          <span v-else class="text-truncate text-muted">{{ placeholder }}</span>
          <font-awesome-icon
            :icon="['fal', isOpen ? 'chevron-up' : 'chevron-down']"
            class="multi-select__chevron ml-2"
          />
        </div>
      </template>

      <template #default>
        <b-dropdown-form @submit.prevent="$dropdown.hide(true)">
          <div class="multi-select__panel">
            <app-input
              v-if="searchable"
              ref="searchInput"
              v-model="searchTerm"
              type="search"
              :placeholder="searchPlaceholder"
              class="multi-select__search-input"
              :validate-immediate="false"
            />

            <div
              v-if="selectAll"
              class="multi-select__row multi-select__row--all"
              @click.stop.prevent="toggleSelectAll"
            >
              <b-form-checkbox :checked="isAllSelectedUi" @change.stop.prevent="toggleSelectAll">
                {{ selectAllText }}
              </b-form-checkbox>
            </div>

            <div class="multi-select__divider" v-if="selectAll"></div>

            <div class="multi-select__list">
              <template v-if="searchable">
                <div v-if="!meetsMinSearchLength">
                  <small class="multi-select__helper-text">{{ searchHelperText }}</small>
                </div>
                <div v-else-if="displayedOptions.length === 0">
                  <small class="multi-select__helper-text">{{ noResultsText }}</small>
                </div>
                <app-input v-else v-model="listValue" type="checkbox" :options="displayedOptions" stacked />
              </template>
              <template v-else>
                <app-input v-model="listValue" type="checkbox" :options="options" stacked />
              </template>
            </div>
          </div>
        </b-dropdown-form>
      </template>
    </b-dropdown>
  </b-form-group>
</template>

<script setup>
import { computed, ref, nextTick } from 'vue';
import AppInput from '@/components/AppInput.vue';

const props = defineProps({
  id: { type: String, required: true },
  label: String,
  placeholder: { type: String, default: 'Select...' },
  options: { type: Array, required: true }, // [{ text, value }]
  value: { type: Array, required: true },
  selectAll: { type: Boolean, default: true },
  selectAllText: { type: String, default: 'Select all' },
  disabled: { type: Boolean, default: false },
  // When true, an empty selection represents "all" (future-proof).
  // UI will display a single badge with selectAllText when empty (or all selected),
  // and the v-model will normalize "all selected" to [] on write.
  emptyMeansAll: { type: Boolean, default: false },
  // When true, show a search input inside the dropdown panel and filter options by text.
  searchable: { type: Boolean, default: false },
  searchPlaceholder: { type: String, default: 'Search...' },
  searchMinChars: { type: Number, default: 3 },
  searchHelperText: {
    type: String,
    default: 'Please enter at least 3 characters',
  },
  noResultsText: { type: String, default: 'No options found' },
});

const emit = defineEmits(['input']);

const $dropdown = ref(undefined);
const isOpen = ref(false);
const searchInput = ref(undefined);
const searchTerm = ref('');

const listValue = computed({
  get: () => props.value,
  set: v => emit('input', v),
});

const isNoOptions = computed(() => !(props.options?.length > 0));
const isDisabled = computed(() => props.disabled || isNoOptions.value);

const trimmedSearch = computed(() => searchTerm.value.trim());
const meetsMinSearchLength = computed(() => props.searchable && trimmedSearch.value.length >= props.searchMinChars);

const displayedOptions = computed(() => {
  // When not searchable, always show all options
  if (!props.searchable) {
    return props.options || [];
  }

  // If search text is shorter than minimum, hide all options
  if (!meetsMinSearchLength.value) {
    return [];
  }

  const term = trimmedSearch.value.toLowerCase();
  return (props.options || []).filter(o =>
    String(o.text || '')
      .toLowerCase()
      .includes(term),
  );
});

const isAllSelected = computed(() => {
  if (!props.options?.length) {
    return false;
  }
  return props.options.every(o => props.value.includes(o.value));
});

const isAllSelectedUi = computed(() => {
  if (!props.options?.length) {
    return false;
  }
  // When emptyMeansAll is true, only show "all" UI when selection is truly empty.
  // Explicitly selected items (even if all of them) should show individually.
  if (props.emptyMeansAll) {
    return !hasSelection.value;
  }
  return isAllSelected.value;
});

const hasSelection = computed(() => (props.value?.length || 0) > 0);

const selectedOptions = computed(() => props.options.filter(o => props.value.includes(o.value)));

const focusSearchInput = () => {
  if (!props.searchable) {
    return;
  }

  nextTick(() => {
    const el =
      searchInput.value?.$el?.querySelector?.('input, textarea, select') ||
      searchInput.value?.$el?.querySelector?.('.form-control');
    if (el && typeof el.focus === 'function') {
      el.focus();
    }
  });
};

const onDropdownShown = () => {
  focusSearchInput();
};

const toggleSelectAll = () => {
  if (!props.options?.length) {
    return;
  }
  // With emptyMeansAll, "all" is represented by an empty array — so clicking the
  // Select all row always normalises to [] (whether the user had nothing, some,
  // or everything explicitly selected). The early return prevents a no-op edit
  // when already empty.
  if (props.emptyMeansAll) {
    if (hasSelection.value) {
      listValue.value = [];
    }
    return;
  }
  if (isAllSelected.value) {
    listValue.value = [];
  } else {
    listValue.value = props.options.map(o => o.value);
  }
};

const removeValue = value => {
  listValue.value = props.value.filter(v => v !== value);
};
</script>

<style lang="scss" scoped>
:deep(.multi-select__label) {
  color: var(--palette-color-default-lighten-20);
  font-weight: 500;

  > small {
    color: var(--palette-color-default-lighten-30);
    display: block;
    font-style: normal;
    margin-top: 0.1rem;
  }
}

.multi-select__dropdown :deep(.dropdown-menu) {
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
}

/* Remove default padding from the inner dropdown form */
.multi-select__dropdown :deep(form) {
  padding: 0.7rem 0;
}

/* Ensure the toggle behaves like a text input and override btn-light hover */
.multi-select__dropdown :deep(.multi-select__toggle) {
  background-color: var(--palette-color-base-white);
  padding: 0.6rem 0.875rem;
  border-radius: 0.5rem;
  border: 1px solid var(--palette-color-default-lighten-90);
  transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out;
  height: auto;
  min-height: 2.375rem;
}

.multi-select__dropdown :deep(.multi-select__toggle:hover),
.multi-select__dropdown :deep(.show .multi-select__toggle),
.multi-select__dropdown :deep(.multi-select__toggle:focus),
.multi-select__dropdown :deep(.multi-select__toggle:active),
.multi-select__dropdown :deep(.show .multi-select__toggle:active),
.multi-select__dropdown :deep(.multi-select__toggle[aria-expanded='true']),
.multi-select__dropdown :deep(.multi-select__toggle[aria-expanded='true']:active) {
  background-color: var(--palette-color-base-white) !important; /* prevent btn-light hover background */
  border-color: var(--palette-color-brand-primary) !important; /* primary border */
  box-shadow: none !important;
}

/* When disabled, keep base styling and suppress hover/focus/open visuals */
.multi-select__dropdown :deep(.multi-select__toggle[disabled]),
.multi-select__dropdown :deep(.multi-select__toggle.disabled),
.multi-select__dropdown :deep([aria-disabled='true'] .multi-select__toggle),
.multi-select__dropdown :deep(.multi-select__toggle[disabled]:hover),
.multi-select__dropdown :deep(.multi-select__toggle.disabled:hover),
.multi-select__dropdown :deep([aria-disabled='true'] .multi-select__toggle:hover),
.multi-select__dropdown :deep(.multi-select__toggle[disabled]:focus),
.multi-select__dropdown :deep(.multi-select__toggle.disabled:focus),
.multi-select__dropdown :deep([aria-disabled='true'] .multi-select__toggle:focus),
.multi-select__dropdown :deep(.multi-select__toggle[disabled]:active),
.multi-select__dropdown :deep(.multi-select__toggle.disabled:active),
.multi-select__dropdown :deep([aria-disabled='true'] .multi-select__toggle:active) {
  background-color: var(--palette-color-base-white) !important;
  border-color: var(--palette-color-default-lighten-90) !important;
  box-shadow: none !important;
}

/* Reset Bootstrap's badge nudge inside the toggle */
.multi-select__dropdown :deep(.multi-select__toggle .multi-select__tag) {
  position: static; /* or keep relative and set top: 0 */
  top: 0;
}

/* Using the default caret; no custom rotation needed */
.multi-select__panel {
  background: var(--palette-color-base-white);
  border-radius: 0.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  min-width: 30rem;
  overflow: hidden;
}

.multi-select__panel :deep(.multi-select__search-input) {
  margin-bottom: 0;
}

.multi-select__panel :deep(.multi-select__search-input .input-group),
.multi-select__panel :deep(.multi-select__search-input .form-control) {
  border-radius: 0.5rem 0.5rem 0 0;
}

.multi-select__row {
  padding: 0.75rem 1rem;
  display: flex;
}

.multi-select__row--all {
  cursor: pointer;
}

.multi-select__divider {
  border-top: 1px solid var(--palette-color-default-lighten-90);
}

.multi-select__list {
  max-height: 20rem;
  overflow: auto;
  padding: 0.75rem 1rem;
}

.multi-select__tags {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.multi-select__tag {
  cursor: default;
  border: 1px solid var(--palette-color-default-lighten-90);
}

.multi-select__chevron {
  color: var(--palette-color-default-lighten-40);
}

/* Enlarge all checkboxes within the dropdown to 2rem */
.multi-select__panel :deep(.custom-control.custom-checkbox .custom-control-label::before),
.multi-select__panel :deep(.custom-control.custom-checkbox .custom-control-label::after) {
  width: 2rem;
  height: 2rem;
  border-radius: 0.25rem;
  border-width: 1px;
}

.multi-select__panel :deep(.custom-control.custom-checkbox .custom-control-label) {
  font-size: 14px;
  padding-left: 1rem;
  line-height: 2rem;
  user-select: none;
}

/* Bootstrap 5 form-check variant */
.multi-select__panel :deep(.form-check-input) {
  width: 2rem;
  height: 2rem;
}

.multi-select__panel :deep(.form-check-label) {
  font-size: 14px;
  line-height: 2rem;
  user-select: none;
}

/* Spacing between option rows */
.multi-select__list :deep(.custom-control),
.multi-select__list :deep(.form-check) {
  margin-bottom: 0.75rem;
}

.multi-select__list :deep(.custom-control:last-child),
.multi-select__list :deep(.form-check:last-child) {
  margin-bottom: 0;
}
</style>
